import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Envase.css';

function TFPago() {
  const [datos, setDatos] = useState([]);
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [ordenAsc, setOrdenAsc] = useState(true);
  const [columnaOrden, setColumnaOrden] = useState('idFpago');

  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [itemsPorPagina, setItemsPorPagina] = useState(10);

  // Modal
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [formulario, setFormulario] = useState({
    idFpago: '',
    cancuota: '',
    vdias1: '0',
    vdias2: '0',
    vdias3: '0'
  });
  const [errores, setErrores] = useState({});

  const API_URL = '/api/fpago';

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    filtrarDatos();
    setPaginaActual(1); // Resetear a página 1 cuando cambia la búsqueda
  }, [busqueda, datos]);

  const cargarDatos = async () => {
    try {
      const response = await axios.get(API_URL);
      setDatos(response.data);
      setDatosFiltrados(response.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('Error al cargar los datos');
    }
  };

  const filtrarDatos = () => {
    if (!busqueda) {
      setDatosFiltrados(datos);
      return;
    }

    const filtrados = datos.filter(item =>
      item.idFpago.toLowerCase().includes(busqueda.toLowerCase()) ||
      item.cancuota.toString().includes(busqueda)
    );
    setDatosFiltrados(filtrados);
  };

  const ordenarPor = (columna) => {
    const esAsc = columnaOrden === columna ? !ordenAsc : true;
    setColumnaOrden(columna);
    setOrdenAsc(esAsc);

    const ordenados = [...datosFiltrados].sort((a, b) => {
      let valorA = a[columna];
      let valorB = b[columna];

      // Para strings
      if (typeof valorA === 'string') {
        valorA = valorA.toLowerCase();
        valorB = valorB.toLowerCase();
      }

      if (esAsc) {
        return valorA > valorB ? 1 : -1;
      } else {
        return valorA < valorB ? 1 : -1;
      }
    });

    setDatosFiltrados(ordenados);
  };

  const exportarExcel = () => {
    const datosExportar = datosFiltrados.map(item => ({
      'ID Forma Pago': item.idFpago,
      'Cantidad Cuotas': item.cancuota,
      'Venc. Días 1': item.vdias1,
      'Venc. Días 2': item.vdias2,
      'Venc. Días 3': item.vdias3
    }));

    const ws = XLSX.utils.json_to_sheet(datosExportar);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Formas de Pago');
    XLSX.writeFile(wb, 'formas_pago.xlsx');
  };

  const abrirModalNuevo = () => {
    setFormulario({
      idFpago: '',
      cancuota: '',
      vdias1: '0',
      vdias2: '0',
      vdias3: '0'
    });
    setModoEdicion(false);
    setModalAbierto(true);
  };

  const abrirModalEditar = (item) => {
    setFormulario({
      idFpago: item.idFpago,
      cancuota: item.cancuota.toString(),
      vdias1: item.vdias1.toString(),
      vdias2: item.vdias2.toString(),
      vdias3: item.vdias3.toString()
    });
    setModoEdicion(true);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setFormulario({
      idFpago: '',
      cancuota: '',
      vdias1: '0',
      vdias2: '0',
      vdias3: '0'
    });
    setErrores({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormulario({
      ...formulario,
      [name]: value
    });
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errores[name]) {
      setErrores({
        ...errores,
        [name]: ''
      });
    }
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    // Validar ID (requerido, máximo 30 caracteres)
    if (!modoEdicion) {
      if (!formulario.idFpago.trim()) {
        nuevosErrores.idFpago = 'El ID es requerido';
      } else if (formulario.idFpago.trim().length > 30) {
        nuevosErrores.idFpago = 'El ID no puede tener más de 30 caracteres';
      } else {
        // Verificar duplicados
        const existe = datos.find(item =>
          item.idFpago.toLowerCase() === formulario.idFpago.trim().toLowerCase()
        );
        if (existe) {
          nuevosErrores.idFpago = 'Ya existe una forma de pago con este ID';
        }
      }
    }

    // Validar cantidad de cuotas (requerido y mayor a 0)
    if (!formulario.cancuota) {
      nuevosErrores.cancuota = 'La cantidad de cuotas es requerida';
    } else if (parseInt(formulario.cancuota) <= 0) {
      nuevosErrores.cancuota = 'La cantidad de cuotas debe ser mayor a 0';
    }

    // Validar vdias1, vdias2, vdias3 (deben ser números >= 0)
    if (formulario.vdias1 && parseInt(formulario.vdias1) < 0) {
      nuevosErrores.vdias1 = 'Los días deben ser mayor o igual a 0';
    }
    if (formulario.vdias2 && parseInt(formulario.vdias2) < 0) {
      nuevosErrores.vdias2 = 'Los días deben ser mayor o igual a 0';
    }
    if (formulario.vdias3 && parseInt(formulario.vdias3) < 0) {
      nuevosErrores.vdias3 = 'Los días deben ser mayor o igual a 0';
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar formulario antes de enviar
    if (!validarFormulario()) {
      return;
    }

    // Preparar datos para enviar
    const datosEnviar = {
      idFpago: formulario.idFpago.trim().toUpperCase(),
      cancuota: parseInt(formulario.cancuota),
      vdias1: parseInt(formulario.vdias1) || 0,
      vdias2: parseInt(formulario.vdias2) || 0,
      vdias3: parseInt(formulario.vdias3) || 0
    };

    try {
      if (modoEdicion) {
        await axios.put(`${API_URL}/${formulario.idFpago}`, datosEnviar);
        alert('Forma de pago actualizada exitosamente');
      } else {
        await axios.post(API_URL, datosEnviar);
        alert('Forma de pago creada exitosamente');
      }
      cargarDatos();
      cerrarModal();
    } catch (error) {
      console.error('Error al guardar:', error);
      if (error.response) {
        alert(`Error al guardar: ${error.response.data.message || 'Error del servidor'}`);
      } else if (error.request) {
        alert('Error de conexión con el servidor');
      } else {
        alert('Error al guardar la forma de pago');
      }
    }
  };

  const eliminar = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta forma de pago?')) {
      try {
        await axios.delete(`${API_URL}/${id}`);
        alert('Forma de pago eliminada exitosamente');
        cargarDatos();
      } catch (error) {
        console.error('Error al eliminar:', error);
        alert('Error al eliminar la forma de pago');
      }
    }
  };

  // Lógica de paginación
  const indexUltimoItem = paginaActual * itemsPorPagina;
  const indexPrimerItem = indexUltimoItem - itemsPorPagina;
  const itemsActuales = datosFiltrados.slice(indexPrimerItem, indexUltimoItem);
  const totalPaginas = Math.ceil(datosFiltrados.length / itemsPorPagina);

  const cambiarPagina = (numeroPagina) => {
    setPaginaActual(numeroPagina);
  };

  const paginaAnterior = () => {
    if (paginaActual > 1) {
      setPaginaActual(paginaActual - 1);
    }
  };

  const paginaSiguiente = () => {
    if (paginaActual < totalPaginas) {
      setPaginaActual(paginaActual + 1);
    }
  };

  return (
    <div className="envase-container">
      <div className="header-seccion">
        <h2>Formas de Pago</h2>
        <div className="acciones-header">
          <input
            type="text"
            placeholder="Buscar..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="input-busqueda"
          />
          <button onClick={abrirModalNuevo} className="btn btn-nuevo">
            ➕ Nuevo
          </button>
          <button onClick={exportarExcel} className="btn btn-exportar">
            📊 Exportar Excel
          </button>
        </div>
      </div>

      <div className="tabla-container">
        <table className="tabla-datos">
          <thead>
            <tr>
              <th onClick={() => ordenarPor('idFpago')} className="th-ordenable">
                ID Forma Pago {columnaOrden === 'idFpago' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th onClick={() => ordenarPor('cancuota')} className="th-ordenable">
                Cant. Cuotas {columnaOrden === 'cancuota' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th onClick={() => ordenarPor('vdias1')} className="th-ordenable">
                Venc. Días 1 {columnaOrden === 'vdias1' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th onClick={() => ordenarPor('vdias2')} className="th-ordenable">
                Venc. Días 2 {columnaOrden === 'vdias2' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th onClick={() => ordenarPor('vdias3')} className="th-ordenable">
                Venc. Días 3 {columnaOrden === 'vdias3' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {itemsActuales.length === 0 ? (
              <tr>
                <td colSpan="6" className="sin-datos">No hay datos para mostrar</td>
              </tr>
            ) : (
              itemsActuales.map(item => (
                <tr key={item.idFpago}>
                  <td>{item.idFpago}</td>
                  <td style={{ textAlign: 'center' }}>{item.cancuota}</td>
                  <td style={{ textAlign: 'center' }}>{item.vdias1}</td>
                  <td style={{ textAlign: 'center' }}>{item.vdias2}</td>
                  <td style={{ textAlign: 'center' }}>{item.vdias3}</td>
                  <td>
                    <button
                      onClick={() => abrirModalEditar(item)}
                      className="btn-accion btn-editar"
                      title="Editar"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => eliminar(item.idFpago)}
                      className="btn-accion btn-eliminar"
                      title="Eliminar"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="footer-tabla">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Total de registros: {datosFiltrados.length}</span>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label>
              Mostrar:
              <select
                value={itemsPorPagina}
                onChange={(e) => {
                  setItemsPorPagina(Number(e.target.value));
                  setPaginaActual(1);
                }}
                style={{ marginLeft: '5px', padding: '5px' }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </label>

            <div style={{ display: 'flex', gap: '5px' }}>
              <button
                onClick={paginaAnterior}
                disabled={paginaActual === 1}
                className="btn"
                style={{ padding: '5px 10px' }}
              >
                ← Anterior
              </button>

              {[...Array(totalPaginas)].map((_, index) => {
                const numeroPagina = index + 1;
                // Mostrar solo algunas páginas alrededor de la actual
                if (
                  numeroPagina === 1 ||
                  numeroPagina === totalPaginas ||
                  (numeroPagina >= paginaActual - 1 && numeroPagina <= paginaActual + 1)
                ) {
                  return (
                    <button
                      key={numeroPagina}
                      onClick={() => cambiarPagina(numeroPagina)}
                      className={paginaActual === numeroPagina ? 'btn btn-activo' : 'btn'}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: paginaActual === numeroPagina ? '#001A70' : '',
                        color: paginaActual === numeroPagina ? 'white' : ''
                      }}
                    >
                      {numeroPagina}
                    </button>
                  );
                } else if (
                  numeroPagina === paginaActual - 2 ||
                  numeroPagina === paginaActual + 2
                ) {
                  return <span key={numeroPagina}>...</span>;
                }
                return null;
              })}

              <button
                onClick={paginaSiguiente}
                disabled={paginaActual === totalPaginas}
                className="btn"
                style={{ padding: '5px 10px' }}
              >
                Siguiente →
              </button>
            </div>

            <span>
              Página {paginaActual} de {totalPaginas || 1}
            </span>
          </div>
        </div>
      </div>

      {/* Modal */}
      {modalAbierto && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-contenido" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modoEdicion ? 'Editar Forma de Pago' : 'Nueva Forma de Pago'}</h3>
              <button onClick={cerrarModal} className="btn-cerrar">✖</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>ID Forma de Pago *</label>
                <input
                  type="text"
                  name="idFpago"
                  value={formulario.idFpago}
                  onChange={handleChange}
                  maxLength="30"
                  disabled={modoEdicion}
                  className={`form-input ${errores.idFpago ? 'form-input-error' : ''}`}
                  placeholder="Máximo 30 caracteres"
                />
                {errores.idFpago && (
                  <small className="form-error">{errores.idFpago}</small>
                )}
              </div>

              <div className="form-group">
                <label>Cantidad de Cuotas *</label>
                <input
                  type="number"
                  name="cancuota"
                  value={formulario.cancuota}
                  onChange={handleChange}
                  min="1"
                  className={`form-input ${errores.cancuota ? 'form-input-error' : ''}`}
                />
                {errores.cancuota && (
                  <small className="form-error">{errores.cancuota}</small>
                )}
              </div>

              <div className="form-group">
                <label>Vencimiento Días 1</label>
                <input
                  type="number"
                  name="vdias1"
                  value={formulario.vdias1}
                  onChange={handleChange}
                  min="0"
                  className={`form-input ${errores.vdias1 ? 'form-input-error' : ''}`}
                />
                {errores.vdias1 && (
                  <small className="form-error">{errores.vdias1}</small>
                )}
              </div>

              <div className="form-group">
                <label>Vencimiento Días 2</label>
                <input
                  type="number"
                  name="vdias2"
                  value={formulario.vdias2}
                  onChange={handleChange}
                  min="0"
                  className={`form-input ${errores.vdias2 ? 'form-input-error' : ''}`}
                />
                {errores.vdias2 && (
                  <small className="form-error">{errores.vdias2}</small>
                )}
              </div>

              <div className="form-group">
                <label>Vencimiento Días 3</label>
                <input
                  type="number"
                  name="vdias3"
                  value={formulario.vdias3}
                  onChange={handleChange}
                  min="0"
                  className={`form-input ${errores.vdias3 ? 'form-input-error' : ''}`}
                />
                {errores.vdias3 && (
                  <small className="form-error">{errores.vdias3}</small>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={cerrarModal} className="btn btn-cancelar">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-guardar">
                  {modoEdicion ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TFPago;
